import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { ModalStackProvider } from "@/context/ModalStackContext";
import { GoogleOAuthProvider } from '@react-oauth/google';
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import ProjectsList from "@/pages/ProjectsList";
import ProjectDetails from "@/pages/ProjectDetails";
import Account from "@/pages/Account";
import CalendarPage from "@/pages/CalendarPage";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import BackButtonHandler from "./components/BackButtonHandler";
import ResetPassword from "./components/ResetPassword";
import SharedEventView from "@/pages/SharedEventView";

const queryClient = new QueryClient();

// Separate component for router content that depends on context
const RouterContent = () => (
  <BrowserRouter>
    <BackButtonHandler />
    <Routes>
      <Route path="/share/event/:token" element={<SharedEventView />} />
      <Route
        path="/*"
        element={
          <AppLayout>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><ProjectsList /></ProtectedRoute>} />
              <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
              <Route path="/projects/:id/:eventId" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppLayout>
        }
      />
    </Routes>
  </BrowserRouter>
);

const App = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    SocialLogin.initialize({
      google: {
        webClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        mode: 'online',
      },
    }).catch((e) => console.warn('SocialLogin.initialize failed', e));
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AppProvider>
            <ModalStackProvider>
              <Toaster />
              <Sonner />
              <RouterContent />
            </ModalStackProvider>
          </AppProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
